<template>
  <div ref="pdfContent" class="pdf-content">
    <CCard class="border-0">
      <CCardHeader class="no-print bg-transparent">
        <div class="d-flex justify-content-end">
          <CButton
            color="success"
            size="sm"
            class="mr-2"
            @click="exportPDF"
            :disabled="isGeneratingPDF"
          >
            <CIcon name="cil-cloud-download" class="mr-1" />
            {{ isGeneratingPDF ? 'Generating...' : 'Download PDF' }}
          </CButton>
          <CButton color="info" size="sm" @click="printContent">
            <CIcon name="cil-print" class="mr-1" /> Print
          </CButton>
        </div>
      </CCardHeader>
      
      <CCardBody class="p-4">
        <!-- Header Section -->
        <div class="document-header">
          <CRow class="justify-content-between align-items-start mb-4">
            <CCol>
              <img
                v-if="getImageUrl()"
                :src="getImageUrl()"
                alt="Company Logo"
                class="company-logo"
              />
            </CCol>
            <CCol class="text-right">
              <h1 class="document-title mb-2">QUOTATION</h1>
              <h5 class="company-name mb-1">{{ quotation.business?.name }}</h5>
              <p class="company-reg mb-1">{{ quotation.business?.regNo ? `(${quotation.business.regNo})` : '' }}</p>
              <div class="company-address mb-2" v-if="quotation.business?.address" v-html="formatAddress(quotation.business.address)"></div>
              <p class="company-phone" v-if="quotation.business?.phone">Tel: {{ quotation.business.phone }}</p>
            </CCol>
          </CRow>

          <hr class="section-divider" />
        </div>

        <!-- Customer and Document Details -->
        <div class="document-details mb-4">
          <CRow>
            <CCol xs="12" md="6" class="mb-3 mb-md-0">
              <div class="customer-section">
                <h6 class="section-title">Bill To:</h6>
                <p class="customer-name mb-1">
                  <strong>{{ getCustomerName() }}</strong>
                </p>
                <div 
                  class="customer-address mb-2" 
                  v-if="getCustomerAddress()" 
                  v-html="formatAddress(getCustomerAddress())"
                ></div>
                <p class="customer-contact" v-if="getCustomerContactPersonName()">
                  Attn: <strong>{{ getCustomerContactPersonName() }}</strong>
                </p>
              </div>
            </CCol>
            
            <CCol xs="12" md="6">
              <div class="document-info">
                <table class="info-table">
                  <tbody>
                    <tr>
                      <td class="info-label">Quotation No:</td>
                      <td class="info-value">{{ quotation.quotationNumber }}</td>
                    </tr>
                    <tr v-if="showIssueDate">
                      <td class="info-label">Issue Date:</td>
                      <td class="info-value">{{ getQuotationIssuedDate() }}</td>
                    </tr>
                    <tr v-if="showDueDate">
                      <td class="info-label">Expiry Date:</td>
                      <td class="info-value">{{ getQuotationExpiryDate() }}</td>
                    </tr>
                    <tr v-if="quotation.reference">
                      <td class="info-label">Reference:</td>
                      <td class="info-value">{{ quotation.reference }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCol>
          </CRow>
          
          <CRow v-if="quotation.title" class="mt-3">
            <CCol>
              <div class="quotation-title">
                <p><strong>Title: </strong>{{ quotation.title }}</p>
              </div>
            </CCol>
          </CRow>
        </div>

        <!-- Items Table -->
        <div class="items-section mb-4">
          <!-- Main Table (Desktop + Print/PDF) -->
          <div class="desktop-table">
            <table class="items-table table table-bordered">
              <thead>
                <tr>
                  <th class="text-center" style="width: 50px">#</th>
                  <th>Item & Description</th>
                  <th class="text-center" style="width: 100px">Quantity</th>
                  <th class="text-center" style="width: 80px">Unit</th>
                  <th class="text-right" style="width: 120px">
                    Price (RM)
                  </th>
                  <th class="text-right" style="width: 120px">
                    Total (RM)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in computedQuotationItems" :key="item.id || item.position">
                  <td class="text-center">{{ item.position }}</td>
                  <td>
                    <div class="item-name mb-1">
                      <strong>{{ item.product?.name }}</strong>
                    </div>
                    <div 
                      class="item-description" 
                      v-if="item.description"
                      v-html="formatDescription(item.description)"
                    ></div>
                  </td>
                  <td class="text-center">{{ item.quantity }}</td>
                  <td class="text-center">{{ item.unit }}</td>
                  <td class="text-right">{{ formatCurrency(item.price) }}</td>
                  <td class="text-right">{{ formatCurrency(item.totalAmountPerItem) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-total">
                  <td colspan="5" class="text-right"><strong>Subtotal</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(grandTotal) }}</strong></td>
                </tr>
                <tr class="table-tax">
                  <td colspan="4" class="text-right"><strong>Tax</strong></td>
                  <td class="text-right"><strong>{{ getTaxName() }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(grandTaxOnly) }}</strong></td>
                </tr>
                <tr class="table-grand-total">
                  <td colspan="5" class="text-right"><strong>Total (with Tax)</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(grandTotalWithTax) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Mobile Cards (Only for small screens on web) -->
          <div class="mobile-cards d-md-none">
            <div 
              v-for="item in computedQuotationItems" 
              :key="item.id || item.position"
              class="item-card mb-3 p-3 border rounded"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="item-header">
                  <span class="item-number">#{{ item.position }}</span>
                  <h6 class="item-name mb-1">{{ item.product?.name }}</h6>
                </div>
                <div class="item-total text-right">
                  <small class="text-muted">Total</small>
                  <div class="font-weight-bold">{{ formatCurrency(item.totalAmountPerItem) }}</div>
                </div>
              </div>
              
              <div class="item-description mb-2" v-if="item.description" v-html="formatDescription(item.description)"></div>
              
              <CRow class="item-details">
                <CCol xs="4">
                  <small class="text-muted">Quantity</small>
                  <div>{{ item.quantity }}</div>
                </CCol>
                <CCol xs="4">
                  <small class="text-muted">Unit</small>
                  <div>{{ item.unit }}</div>
                </CCol>
                <CCol xs="4">
                  <small class="text-muted">Price</small>
                  <div>{{ formatCurrency(item.price) }}</div>
                </CCol>
              </CRow>
            </div>

            <!-- Mobile Summary -->
            <div class="mobile-summary p-3 bg-light rounded">
              <div class="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <span class="font-weight-bold">{{ formatCurrency(grandTotal) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Tax ({{ getTaxName() }}):</span>
                <span class="font-weight-bold">{{ formatCurrency(grandTaxOnly) }}</span>
              </div>
              <hr class="my-2">
              <div class="d-flex justify-content-between">
                <span class="font-weight-bold">Total (with Tax):</span>
                <span class="font-weight-bold h5">{{ formatCurrency(grandTotalWithTax) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Terms and Conditions -->
        <div v-if="quotation.note" class="terms-section mb-4">
          <h6 class="section-title mb-2">Terms and Conditions</h6>
          <div class="terms-content" v-html="formatNote(quotation.note)"></div>
        </div>

        <!-- Footer -->
        <div class="document-footer text-center">
          <hr class="footer-divider">
          <div class="footer-content">
            <p class="company-info mb-1">
              {{ quotation.business?.name }} | {{ quotation.business?.website }} | {{ quotation.business?.phone }}
            </p>
            <p class="powered-by mb-0">
              Powered By 
              <img src="/logo.png" alt="Niaga-ku Logo" class="footer-logo" />
              Niaga-Ku.com
            </p>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";
import html2pdf from "html2pdf.js";

export default {
  name: "WidgetsReportQuotation",
  props: {
    quotation: {
      type: Object,
      required: true,
    },
    showIssueDate: {
      type: Boolean,
      default: true,
    },
    showDueDate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
    };
  },
  computed: {
    computedQuotationItems() {
      return (this.quotation.items || []).map((item) => {
        return {
          ...item,
          productName: item.product?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotalWithTax() {
      return this.grandTotal + this.grandTaxOnly;
    },
    grandTaxOnly() {
      try {
        return (this.quotation.salesTax?.rateInPercentage * this.grandTotal) / 100 || 0;
      } catch (error) {
        return 0;
      }
    },
    grandTotal() {
      return this.computedQuotationItems.reduce((acc, item) => {
        return acc + (item.totalAmountPerItem || 0);
      }, 0);
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
  },
  mounted() {
    this.forceA4Size();
  },
  methods: {
    // Amount without currency; the column headers say RM
    formatCurrency(amount) {
      return formatMyr(amount);
    },

    getTaxName() {
      try {
        return this.quotation.salesTax?.name || "0%";
      } catch (error) {
        return "0%";
      }
    },

    formatNote(note) {
      return note ? note.replace(/\n/g, "<br />") : "";
    },

    formatDescription(description) {
      if (!description) return "";
      return description
        .split("\n")
        .map((line) => {
          const indentLevel = (line.match(/^(\s+)/) || [""])[0].length / 4;
          return `<p style="padding-left: ${
            indentLevel * 20
          }px; margin-bottom: 0.5rem; page-break-inside: avoid;">${line.trim()}</p>`;
        })
        .join("");
    },

    getTotalItemPrice(item) {
      try {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        return quantity * price;
      } catch (error) {
        return 0;
      }
    },

    getQuotationIssuedDate() {
      return this.quotation.issuedDate 
        ? formatDate(this.quotation.issuedDate)
        : "";
    },

    getQuotationExpiryDate() {
      return this.quotation.expiryDate || this.quotation.dueDate
        ? formatDate(this.quotation.expiryDate || this.quotation.dueDate)
        : "";
    },

    getCustomerAddress() {
      try {
        return this.quotation.customer?.address || "";
      } catch (error) {
        return "";
      }
    },

    getCustomerContactPersonName() {
      try {
        return this.quotation.contactName || this.quotation.customer?.contactName || "";
      } catch (error) {
        return "";
      }
    },

    getCustomerName() {
      try {
        return this.quotation.customer?.name || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    removeTrailingSlash(str) {
      return str && str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImageUrl() {
      try {
        if (this.quotation.business?.logoUrl) {
          return this.removeTrailingSlash(apiUrl) + this.quotation.business.logoUrl;
        }
        return "";
      } catch (error) {
        return "";
      }
    },

    formatAddress(address) {
      return address ? address.replace(/\n/g, "<br />") : "";
    },

    forceA4Size() {
      const pdfContent = this.$refs.pdfContent;
      if (pdfContent) {
        pdfContent.style.width = "210mm";
        pdfContent.style.minHeight = "297mm";
      }
    },

    printContent() {
      // Mobile users get PDF download instead
      if (this.isMobile) {
        this.exportPDF();
        return;
      }

      // Desktop users get new window print (preserves Vue app)
      this.openPrintWindow();
    },

    openPrintWindow() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      const printContent = this.$refs.pdfContent.innerHTML;

      const printStyles = this.getPrintStyles();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.quotation.business?.shortName || 'Quotation'}_${this.quotation.quotationNumber || 'QUOT'}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              ${printStyles}
            </style>
          </head>
          <body>
            <div class="pdf-content">
              ${printContent}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    },

    async exportPDF() {
      this.isGeneratingPDF = true;

      try {
        // Clone the content and remove no-print elements
        const element = this.$refs.pdfContent.cloneNode(true);
        const noPrintElements = element.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());

        // Apply PDF-specific styling to override mobile styles
        element.style.width = '794px'; // A4 width in pixels
        element.style.minHeight = 'auto';
        element.style.padding = '20px';
        element.style.fontSize = '14px';
        element.style.lineHeight = '1.4';
        element.style.fontFamily = 'Arial, sans-serif';
        element.style.backgroundColor = '#ffffff';

        // Add PDF generation class to override mobile styles
        element.classList.add('pdf-generation');

        // Override mobile styles for consistent PDF output
        const logo = element.querySelector('.company-logo');
        if (logo) {
          logo.style.maxWidth = '180px';
          logo.style.height = 'auto';
          logo.style.display = 'block';
        }

        // Ensure table has proper sizing for PDF
        const table = element.querySelector('.items-table');
        if (table) {
          table.style.width = '100%';
          table.style.fontSize = '13px';
          table.style.minWidth = 'auto';
        }

        // Fix table cells
        const tableCells = element.querySelectorAll('.items-table th, .items-table td');
        tableCells.forEach(cell => {
          cell.style.padding = '8px 12px';
          cell.style.fontSize = '13px';
          cell.style.whiteSpace = 'normal';
        });

        // Fix typography
        const h1 = element.querySelector('.document-title');
        if (h1) {
          h1.style.fontSize = '28px';
          h1.style.margin = '0 0 10px 0';
        }

        const h5 = element.querySelector('.company-name');
        if (h5) {
          h5.style.fontSize = '16px';
          h5.style.margin = '0 0 5px 0';
        }

        // Fix row layouts for proper alignment
        const rows = element.querySelectorAll('.row');
        rows.forEach(row => {
          row.style.display = 'flex';
          row.style.flexWrap = 'wrap';
          row.style.margin = '0 -7.5px';
        });

        // Fix column layouts
        const cols = element.querySelectorAll('.col');
        cols.forEach(col => {
          col.style.flex = '1';
          col.style.padding = '0 7.5px';
        });

        const colXs12 = element.querySelectorAll('.col-xs-12');
        colXs12.forEach(col => {
          col.style.flex = '0 0 100%';
          col.style.maxWidth = '100%';
        });

        const colMd6 = element.querySelectorAll('.col-md-6');
        colMd6.forEach(col => {
          col.style.flex = '0 0 50%';
          col.style.maxWidth = '50%';
          col.style.padding = '0 7.5px';
        });

        // Fix margin-bottom classes for header columns only
        const headerCols = element.querySelectorAll('.document-header .col');
        headerCols.forEach(col => {
          col.style.marginBottom = '0';
        });

        // Fix alignment classes
        const alignStart = element.querySelectorAll('.align-items-start');
        alignStart.forEach(el => {
          el.style.alignItems = 'flex-start';
        });

        const justifyBetween = element.querySelectorAll('.justify-content-between');
        justifyBetween.forEach(el => {
          el.style.justifyContent = 'space-between';
        });

        // Fix text alignment
        const textRight = element.querySelectorAll('.text-right, .text-sm-right');
        textRight.forEach(el => {
          el.style.textAlign = 'right';
        });

        const textLeft = element.querySelectorAll('.text-left');
        textLeft.forEach(el => {
          el.style.textAlign = 'left';
        });

        const textCenter = element.querySelectorAll('.text-center');
        textCenter.forEach(el => {
          el.style.textAlign = 'center';
        });

        // Hide mobile cards in PDF
        const mobileCards = element.querySelectorAll('.mobile-cards');
        mobileCards.forEach(el => {
          el.style.display = 'none';
        });

        // Show desktop table in PDF
        const desktopTable = element.querySelectorAll('.desktop-table');
        desktopTable.forEach(el => {
          el.style.display = 'block';
        });

        // Ensure terms and footer are visible
        const terms = element.querySelector('.terms-section');
        if (terms) {
          terms.style.display = 'block';
          terms.style.marginTop = '20px';
          terms.style.pageBreakInside = 'avoid';
        }

        const footer = element.querySelector('.document-footer');
        if (footer) {
          footer.style.display = 'block';
          footer.style.textAlign = 'center';
          footer.style.marginTop = '30px';
          footer.style.paddingTop = '15px';
          footer.style.borderTop = '1px solid #ccc';
          footer.style.pageBreakInside = 'avoid';
        }

        // Fix footer content
        const footerContent = element.querySelector('.footer-content');
        if (footerContent) {
          const footerPs = footerContent.querySelectorAll('p');
          footerPs.forEach(p => {
            p.style.margin = '5px 0';
            p.style.fontSize = '14px';
            p.style.color = '#333';
          });
        }

        // Fix footer logo
        const footerLogo = element.querySelector('.footer-logo');
        if (footerLogo) {
          footerLogo.style.maxWidth = '20px';
          footerLogo.style.height = 'auto';
          footerLogo.style.verticalAlign = 'middle';
          footerLogo.style.filter = 'grayscale(100%)';
        }

        // Configure html2pdf options
        const opt = {
          margin: [10, 0, 10, 0],  // [top, right, bottom, left] - 10mm top/bottom for page breaks, 0mm left/right (content padding provides spacing)
          filename: `${this.quotation.business?.shortName || 'Quotation'}_${this.quotation.quotationNumber || 'QUOT'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait'
          },
          pagebreak: { mode: ['css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error('PDF generation failed:', error);
        // Fallback to print dialog
        this.openPrintWindow();
      } finally {
        this.isGeneratingPDF = false;
      }
    },

    getPrintStyles() {
      return `
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          padding: 0;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: white;
        }

        .pdf-content {
          width: 794px;
          min-height: 1123px;
          margin: 0 auto;
          padding: 20px;
          background-color: white;
          font-size: 14px;
          line-height: 1.4;
          page-break-inside: avoid;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .no-print {
          display: none !important;
        }

        .company-logo {
          max-width: 180px;
          height: auto;
          display: block;
        }

        .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -7.5px;
        }

        .col {
          flex: 1;
          padding: 0 7.5px;
        }

        .col-xs-12, .col-12 {
          flex: 0 0 100%;
          max-width: 100%;
        }

        .col-sm-6, .col-md-6 {
          flex: 0 0 50%;
          max-width: 50%;
          padding: 0 7.5px;
        }

        .align-items-start {
          align-items: flex-start;
        }

        .justify-content-between {
          justify-content: space-between;
        }

        .mb-3, .mb-sm-0 {
          margin-bottom: 0 !important;
        }

        .img-fluid {
          max-width: 100%;
          height: auto;
        }

        .section-divider {
          border: 2px solid #2c3e50;
          margin: 10px 0;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
          page-break-inside: auto;
        }

        .items-table th,
        .items-table td {
          border: 1px solid #bdc3c7;
          padding: 8px 12px;
          text-align: left;
          vertical-align: top;
          font-size: 13px;
        }

        .items-table th {
          background-color: #34495e;
          color: white;
          font-weight: bold;
          page-break-after: avoid;
        }

        .items-table tr {
          page-break-inside: auto;
        }

        .item-description p {
          page-break-inside: avoid;
          page-break-after: auto;
        }

        .items-table tfoot tr {
          background-color: #ecf0f1;
        }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }
        .text-sm-right { text-align: right; }

        .terms-section {
          margin-top: 20px;
          page-break-inside: avoid;
          background-color: #f8f9fa;
          padding: 1.5rem;
          border-radius: 0.25rem;
        }

        .document-footer {
          text-align: center;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #ccc;
          font-size: 10px;
          page-break-inside: avoid;
        }

        .footer-logo {
          max-width: 18px;
          height: auto;
          vertical-align: middle;
        }

        .document-header {
          margin-bottom: 15px;
          page-break-after: avoid;
        }

        .document-details {
          margin-bottom: 15px;
        }

        .document-title {
          font-size: 28px;
          margin: 0 0 10px 0;
          page-break-after: avoid;
          font-weight: bold;
        }

        .company-name {
          font-size: 16px;
          margin: 0 0 5px 0;
          font-weight: bold;
        }

        .mobile-cards {
          display: none !important;
        }

        .desktop-table {
          display: block !important;
        }

        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          .pdf-content {
            width: 100%;
            margin: 0;
            padding: 15mm;
            box-sizing: border-box;
          }
          .items-table {
            font-size: 12px;
          }
          .items-table th,
          .items-table td {
            padding: 6px 8px;
            font-size: 11px;
          }
        }
      `;
    },
  },
};
</script>

<style scoped>
.pdf-content {
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 20mm;
  box-sizing: border-box;
  background-color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.4;
  color: #333;
}

.document-header {
  margin-bottom: 2rem;
}

.company-logo {
  max-width: 180px;
  height: auto;
}

.document-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.company-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #34495e;
}

.company-reg, .company-address, .company-phone {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-bottom: 0.25rem;
}

.section-divider {
  border: 2px solid #2c3e50;
  margin: 1.5rem 0;
}

.document-details {
  margin-bottom: 2rem;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.customer-name {
  font-size: 1.1rem;
  color: #2c3e50;
}

.customer-address, .customer-contact {
  font-size: 0.9rem;
  color: #5a6c7d;
}

.info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.25rem;
}

.info-label {
  font-weight: 600;
  color: #2c3e50;
  padding-right: 1rem;
  white-space: nowrap;
  vertical-align: top;
}

.info-value {
  color: #5a6c7d;
  vertical-align: top;
}

.quotation-title {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.25rem;
  border-left: 4px solid #3498db;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.items-table th {
  background-color: #34495e;
  color: white;
  font-weight: 600;
  padding: 0.75rem 0.5rem;
  border: 1px solid #2c3e50;
}

.items-table td {
  padding: 0.75rem 0.5rem;
  border: 1px solid #bdc3c7;
  vertical-align: top;
}

.items-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.item-name {
  font-weight: 600;
  color: #2c3e50;
}

.item-description {
  color: #5a6c7d;
  font-size: 0.9rem;
}

.table-total, .table-tax, .table-grand-total {
  background-color: #ecf0f1 !important;
}

.table-grand-total {
  background-color: #d5dbdb !important;
  font-size: 1.1rem;
}

/* Desktop table - always visible except on small mobile screens */
.desktop-table {
  display: block;
}

/* Mobile cards - only show on small screens */
.mobile-cards {
  display: none;
}

/* Mobile Styles */
.item-card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef !important;
}

.item-number {
  background-color: #3498db;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.item-name {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.item-total {
  text-align: right;
}

.item-details {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
}

.item-details small {
  font-weight: 600;
  color: #6c757d;
}

.mobile-summary {
  background-color: #e8f4f8 !important;
  border: 1px solid #3498db;
}

.terms-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.terms-content {
  color: #5a6c7d;
  font-size: 0.9rem;
}

.document-footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
}

.footer-divider {
  border-top: 1px solid #bdc3c7;
  margin-bottom: 1rem;
}

.company-info {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.powered-by {
  color: #7f8c8d;
  font-size: 0.8rem;
}

.footer-logo {
  max-width: 20px;
  height: auto;
  filter: grayscale(100%);
  margin: 0 0.25rem;
  vertical-align: middle;
}

/* PDF Generation Override - ensures consistent PDF output regardless of device */
.pdf-generation {
  width: 794px !important;
  padding: 20px !important;
  font-size: 14px !important;
}

.pdf-generation .company-logo {
  max-width: 180px !important;
}

.pdf-generation .items-table {
  width: 100% !important;
  min-width: auto !important;
  font-size: 13px !important;
}

.pdf-generation .items-table th,
.pdf-generation .items-table td {
  padding: 8px 12px !important;
  font-size: 13px !important;
  white-space: normal !important;
}

.pdf-generation .document-title {
  font-size: 28px !important;
  margin: 0 0 10px 0 !important;
}

.pdf-generation .company-name {
  font-size: 16px !important;
  margin: 0 0 5px 0 !important;
}

.pdf-generation .row {
  display: flex !important;
  flex-wrap: wrap !important;
  margin: 0 -7.5px !important;
}

.pdf-generation .col {
  flex: 1 !important;
  padding: 0 7.5px !important;
}

.pdf-generation .col-xs-12 {
  flex: 0 0 100% !important;
  max-width: 100% !important;
}

.pdf-generation .col-md-6 {
  flex: 0 0 50% !important;
  max-width: 50% !important;
  padding: 0 7.5px !important;
}

.pdf-generation .align-items-center,
.pdf-generation .align-items-start {
  align-items: flex-start !important;
}

.pdf-generation .text-right,
.pdf-generation .text-sm-right {
  text-align: right !important;
}

.pdf-generation .text-left {
  text-align: left !important;
}

.pdf-generation .text-center {
  text-align: center !important;
}

.pdf-generation .mobile-cards {
  display: none !important;
}

.pdf-generation .desktop-table {
  display: block !important;
}

.pdf-generation .item-description p {
  page-break-inside: avoid;
}

.pdf-generation .terms-section {
  page-break-inside: avoid;
}

.pdf-generation .document-footer {
  page-break-inside: avoid;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }

  .pdf-content {
    width: 100%;
    min-height: auto;
    margin: 0;
    padding: 15mm;
    box-shadow: none;
  }

  .document-title {
    font-size: 2rem;
  }

  /* Force desktop table to show in print */
  .desktop-table {
    display: block !important;
  }

  /* Hide mobile cards in print */
  .mobile-cards {
    display: none !important;
  }

  .items-table {
    page-break-inside: auto;
    width: 100%;
    table-layout: fixed;
  }

  .items-table tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  .items-table thead {
    display: table-header-group;
  }

  .items-table tfoot {
    display: table-footer-group;
  }

  .items-table th,
  .items-table td {
    font-size: 10px;
    padding: 6px 4px;
  }

  .items-table th:nth-child(1),
  .items-table td:nth-child(1) {
    width: 8%;
  }

  .items-table th:nth-child(2),
  .items-table td:nth-child(2) {
    width: 40%;
  }

  .items-table th:nth-child(3),
  .items-table td:nth-child(3) {
    width: 12%;
  }

  .items-table th:nth-child(4),
  .items-table td:nth-child(4) {
    width: 10%;
  }

  .items-table th:nth-child(5),
  .items-table td:nth-child(5) {
    width: 15%;
  }

  .items-table th:nth-child(6),
  .items-table td:nth-child(6) {
    width: 15%;
  }

  .terms-section {
    page-break-inside: avoid;
  }

  .document-footer {
    page-break-inside: avoid;
  }
}

/* Responsive Design - Only affect web view */
@media screen and (max-width: 768px) {
  .pdf-content {
    width: 100%;
    padding: 15px;
    min-height: auto;
  }

  .document-title {
    font-size: 1.8rem;
  }

  .company-logo {
    max-width: 150px;
    max-height: 60px;
  }

  .section-title {
    font-size: 0.95rem;
  }

  .info-table {
    font-size: 0.9rem;
  }

  /* Show mobile cards on small screens (web only) */
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
}

@media screen and (max-width: 576px) {
  .pdf-content {
    padding: 10px;
  }

  .document-title {
    font-size: 1.5rem;
  }

  .company-logo {
    max-width: 120px;
    max-height: 50px;
  }

  .quotation-title {
    padding: 0.75rem;
  }

  .terms-section {
    padding: 1rem;
  }

  /* Ensure mobile cards are shown on very small screens (web only) */
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
}
</style>